import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Thread, ThreadMessage } from '../../types/thread';
import { MessageBubble } from './MessageBubble';
import { ChatHandoff, ChatTranscriptEntry } from '../../services/chatApiService';
import { formatDateTime } from '../../utils/contractHelpers';
import { Send, Plus, MoreVertical, Handshake, FileText, UserPlus, MessageCircle, ClipboardPen, ShieldCheck, RotateCcw, ChevronDown, Bell } from 'lucide-react';
import {
    getQuotaStatus,
    recordSend,
    SMS_DAILY_LIMIT,
} from '../../utils/smsReminderQuota';

interface ChatWindowProps {
    thread: Thread;
    messages: ThreadMessage[];
    currentUserId: string;
    currentUserName?: string;
    currentUserAvatar?: string;
    currentUserRole?: 'CLIENT' | 'CREATOR';
    isCurrentUserOfficial?: boolean;
    officialUserId?: string;
    handoff?: ChatHandoff;
    transcript?: ChatTranscriptEntry[];
    isTranscriptLoading?: boolean;
    onLoadTranscript?: (handoffId: string) => void;
    onSendMessage: (content: string) => void;
    onAction: (action: string, payload?: any) => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
    thread,
    messages,
    currentUserId,
    currentUserName,
    currentUserAvatar,
    currentUserRole = 'CLIENT',
    isCurrentUserOfficial = false,
    officialUserId,
    handoff,
    transcript,
    isTranscriptLoading = false,
    onLoadTranscript,
    onSendMessage,
    onAction
}) => {
    const [inputText, setInputText] = useState('');
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(true);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const partner = thread.participants.find(p => p.userId !== currentUserId);
    const currentParticipant = thread.participants.find(p => p.userId === currentUserId);
    const canHandoffToOfficial = Boolean(!isCurrentUserOfficial && partner?.userId && partner.userId !== officialUserId);
    const canReturnToCreator = isCurrentUserOfficial && handoff?.status === 'ACTIVE';

    const handleToggleTranscript = () => {
        if (handoff && !transcript && !isTranscriptLoading) {
            onLoadTranscript?.(handoff.handoffId);
        }
        setIsTranscriptOpen((value) => !value);
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (isCurrentUserOfficial && handoff && isTranscriptOpen && !transcript && !isTranscriptLoading) {
            onLoadTranscript?.(handoff.handoffId);
        }
    }, [handoff, isCurrentUserOfficial, isTranscriptLoading, isTranscriptOpen, onLoadTranscript, transcript]);

    const handleSend = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!inputText.trim()) return;
        onSendMessage(inputText);
        setInputText('');
    };

    // SMS reminder quota — re-evaluated every minute so the cooldown
    // tooltip stays fresh and the button re-enables on its own.
    // Re-evaluate the quota when a send happens (smsTick bump). No timer
    // needed anymore now that there's no interval cooldown to count down.
    const [smsTick, setSmsTick] = useState(0);
    void smsTick;
    const smsQuota = thread.id
        ? getQuotaStatus(currentUserId, thread.id)
        : { canSend: false, remainingToday: 0 };

    const smsTooltip = (() => {
        if (!smsQuota.canSend && smsQuota.reason === 'daily_limit') {
            return `今日提醒已达上限（${SMS_DAILY_LIMIT} 次）`;
        }
        return `发送短信提醒 · 今日剩余 ${smsQuota.remainingToday}/${SMS_DAILY_LIMIT} 次`;
    })();

    const [smsConfirmOpen, setSmsConfirmOpen] = useState(false);
    const openSmsConfirm = () => {
        console.log('[SMS_REMINDER] bell clicked', {
            threadId: thread.id,
            currentUserId,
            partnerUserId: partner?.userId,
            quota: smsQuota,
        });
        if (!thread.id) {
            console.warn('[SMS_REMINDER] aborted: no thread.id');
            return;
        }
        if (!smsQuota.canSend) {
            console.warn('[SMS_REMINDER] aborted: quota.canSend=false', smsQuota);
            return;
        }
        setSmsConfirmOpen(true);
    };
    const confirmSendSmsReminder = () => {
        console.log('[SMS_REMINDER] confirm clicked', {
            threadId: thread.id,
            currentUserId,
            partnerUserId: partner?.userId,
        });
        // Re-check quota in case time elapsed while the modal was open.
        const fresh = getQuotaStatus(currentUserId, thread.id);
        if (!fresh.canSend) {
            console.warn('[SMS_REMINDER] confirm aborted: quota expired while modal open', fresh);
            setSmsConfirmOpen(false);
            setSmsTick(t => t + 1);
            return;
        }
        recordSend(currentUserId, thread.id);
        setSmsTick(t => t + 1);
        setSmsConfirmOpen(false);
        const payload = { threadId: thread.id, partnerUserId: partner?.userId };
        console.log('[SMS_REMINDER] dispatching onAction(SEND_SMS_REMINDER)', payload);
        onAction('SEND_SMS_REMINDER', payload);
    };

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <span
                        className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => partner?.userId && navigate(`/profile/${partner.userId}`)}
                    >
                    <img src={partner?.avatar} alt={partner?.nickname} className="w-10 h-10 rounded-full border border-gray-100" />
                    <div>
                        <h3 className="font-bold text-gray-900">{partner?.nickname}</h3>
                        <p className="text-xs text-gray-500">即时沟通</p>
                    </div>
                    </span>
                </div>

                {/* Contextual Actions */}
                <div className="flex items-center gap-2 relative">
                    <button
                        onClick={() => setIsHeaderMenuOpen(!isHeaderMenuOpen)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
                    >
                        <MoreVertical className="w-5 h-5" />
                    </button>
                    {isHeaderMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setIsHeaderMenuOpen(false)} />
                            <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                                <button
                                    onClick={() => {
                                        onAction('CLEAR_HISTORY');
                                        setIsHeaderMenuOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-red-500 font-medium"
                                >
                                    清空聊天记录
                                </button>
                                {canHandoffToOfficial && (
                                    <button
                                        onClick={() => {
                                            onAction('HANDOFF_TO_OFFICIAL');
                                            setIsHeaderMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-blue-600 font-medium border-t border-gray-50 flex items-center gap-2"
                                    >
                                        <ShieldCheck className="w-4 h-4" />
                                        接入官方
                                    </button>
                                )}
                                {canReturnToCreator && (
                                    <button
                                        onClick={() => {
                                            onAction('RETURN_TO_CREATOR', handoff);
                                            setIsHeaderMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-amber-600 font-medium border-t border-gray-50 flex items-center gap-2"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        交回创作者
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {isCurrentUserOfficial && handoff && (
                <div className="border-b border-gray-100 bg-blue-50/70 px-6 py-3">
                    <button
                        type="button"
                        onClick={handleToggleTranscript}
                        className="w-full flex items-center justify-between text-left"
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                            <ShieldCheck className="w-4 h-4" />
                            交接记录
                            <span className="text-xs font-normal text-blue-700">
                                {handoff.creator?.displayName || handoff.creator?.username || handoff.creatorUserId}
                                {' '}转交，{handoff.transcriptMessageCount} 条原文
                            </span>
                        </span>
                        <ChevronDown className={`w-4 h-4 text-blue-700 transition-transform ${isTranscriptOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isTranscriptOpen && (
                        <div className="mt-3 max-h-52 overflow-y-auto rounded-lg border border-blue-100 bg-white/80 p-3">
                            {isTranscriptLoading ? (
                                <p className="text-sm text-blue-700">正在加载原文记录...</p>
                            ) : transcript && transcript.length > 0 ? (
                                <div className="space-y-3">
                                    {transcript.map((entry) => (
                                        <div key={`${entry.id}_${entry.createdAt}`} className="text-sm">
                                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                                <span className="font-semibold text-gray-600">{entry.senderName || entry.senderId}</span>
                                                <span>{formatDateTime(entry.createdAt)}</span>
                                                <span>{entry.type}</span>
                                            </div>
                                            <p className="mt-1 whitespace-pre-wrap text-gray-700">{entry.content || '[结构化消息]'}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-blue-700">暂无可显示的原文记录</p>
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                        <p>开始此对话的第一次即时沟通</p>
                    </div>
                ) : (
                    messages.map(msg => {
                        const own = msg.senderId === currentUserId;
                        const senderName = own ? (currentUserName || currentParticipant?.nickname) : partner?.nickname;
                        const senderAvatar = own ? (currentUserAvatar || currentParticipant?.avatar) : partner?.avatar;
                        const senderId = own ? currentUserId : (msg.senderId !== 'system' ? msg.senderId : partner?.userId);
                        return (
                            <MessageBubble
                                key={msg.id}
                                message={msg}
                                isOwn={own}
                                senderId={senderId}
                                senderName={senderName}
                                senderAvatar={senderAvatar}
                                onAvatarClick={(userId) => navigate(`/profile/${userId}`)}
                                onAction={onAction}
                            />
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 shrink-0 bg-white">
                <div className="flex items-end gap-3 bg-gray-50 p-2 rounded-2xl border border-gray-100 focus-within:border-blue-200 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                    <div className="relative">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                            title="更多操作"
                        >
                            <Plus className={`w-5 h-5 transition-transform ${isMenuOpen ? 'rotate-45' : ''}`} />
                        </button>

                        {isMenuOpen && (
                            <div className="absolute bottom-full left-0 mb-2 w-[360px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-scale-in origin-bottom-left z-20">
                                <div className="grid grid-cols-3">
                                <button onClick={() => { onAction('OPEN_PROPOSAL_DIALOG'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700 border-r border-b border-gray-50">
                                    <FileText className="w-5 h-5 text-blue-500" />提交方案
                                </button>
                                <button onClick={() => { onAction('SEND_PROPOSAL_REQUEST_MSG'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700 border-r border-b border-gray-50">
                                    <MessageCircle className="w-5 h-5 text-indigo-500" />请求方案
                                </button>
                                <button onClick={() => { onAction('START_COOPERATION'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700 border-b border-gray-50">
                                    <Handshake className="w-5 h-5 text-green-500" />发起合同
                                </button>
                                <button onClick={() => { onAction('INVITE_COOPERATION'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700 border-r border-gray-50">
                                    <UserPlus className="w-5 h-5 text-purple-500" />请求合同
                                </button>
                                <button onClick={() => { onAction('OPEN_TRIAL_REQUEST_DIALOG'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700 border-r border-gray-50">
                                    <ClipboardPen className="w-5 h-5 text-amber-500" />发起试稿
                                </button>
                                {canHandoffToOfficial && (
                                    <button onClick={() => { onAction('HANDOFF_TO_OFFICIAL'); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700">
                                        <ShieldCheck className="w-5 h-5 text-blue-500" />接入官方
                                    </button>
                                )}
                                {canReturnToCreator && (
                                    <button onClick={() => { onAction('RETURN_TO_CREATOR', handoff); setIsMenuOpen(false); }} className="h-24 px-3 py-3 hover:bg-gray-50 flex flex-col items-center justify-center gap-2 text-sm font-medium text-gray-700">
                                        <RotateCcw className="w-5 h-5 text-amber-500" />交回创作者
                                    </button>
                                )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Backdrop for closing menu */}
                    {isMenuOpen && (
                        <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
                    )}
                    <form onSubmit={handleSend} className="flex-1 flex items-end gap-2">
                        <textarea
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={(e) => {
                                // Skip while an IME is composing — Chinese IME fires
                                // an Enter keydown to commit characters; sending then
                                // dispatches stale text and leaves the just-committed
                                // letters orphaned in the input.
                                if ((e as any).nativeEvent?.isComposing || e.keyCode === 229) return;
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            placeholder="输入消息..."
                            className="w-full max-h-32 py-2.5 bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-400 resize-none text-sm"
                            rows={1}
                            style={{ minHeight: '44px' }}
                        />
                        <button
                            type="submit"
                            disabled={!inputText.trim()}
                            className={`p-2.5 rounded-xl transition-all ${inputText.trim() ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                        >
                            <Send className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={openSmsConfirm}
                            disabled={!smsQuota.canSend}
                            title={smsTooltip}
                            className={`p-2.5 rounded-xl transition-all border ${
                                smsQuota.canSend
                                    ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                    : 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                            }`}
                            aria-label="发送短信提醒"
                        >
                            <Bell className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>

            {smsConfirmOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={() => setSmsConfirmOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                                <Bell className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">发送短信提醒</h3>
                                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                    将以短信方式提醒
                                    {partner?.nickname ? <span className="font-medium text-gray-700">「{partner.nickname}」</span> : '对方'}
                                    查看本对话。
                                </p>
                            </div>
                        </div>
                        <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-500 leading-relaxed mb-4">
                            为避免打扰对方，每个对话每天最多发送 {SMS_DAILY_LIMIT} 次。
                            <br />
                            今日剩余 <span className="font-medium text-gray-700">{smsQuota.remainingToday}</span> 次
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setSmsConfirmOpen(false)}
                                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                onClick={confirmSendSmsReminder}
                                className="px-4 py-2 bg-black text-white text-sm rounded-lg hover:bg-gray-800"
                            >
                                确认发送
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
